import React, { Component } from "react"
import { Query } from "react-apollo"
import { Waypoint } from "react-waypoint"
import { debounce } from "throttle-debounce"
import ProductCard from "../../components/ProductCard"
import Favorite, { FavoriteButton } from "../../components/FavoriteButton"
import Dropdown from "../../components/Dropdown"
import SearchBar from "../../components/SearchBar"
import Button from "../../components/Button"
import qs from "qs"
import { Helmet } from "react-helmet"
import PRODUCTS from "../../graphql/products.graphql"
import Loading from "../../components/Loading"
import { filterObjectKeys } from "../../shared/helpers"
import { Redirect } from "react-router"
import { defaultLogo } from "../../shared/images"
import "./marketplace.scss"

class Marketplace extends Component {
  constructor(props) {
    super(props)

    this.state = {
      hasMoreProducts: true,
      searchKeyword: "",
    }

    this.debounceFunc = debounce(300, this.onFinishedTyping)
  }

  searchMarketPlace = (e) => {
    e.persist()
    this.setState({ searchKeyword: e.target.value })

    e.keyCode === 13 ? this.onFinishedTyping() : this.debounceFunc()
  }

  onMagGlassClick = (e) => {
    this.onFinishedTyping()
  }

  onFinishedTyping = () => {
    if (this.state.searchKeyword) {
      this.props.trackEvent("Searched Marketplace", {
        keyword: this.state.searchKeyword,
      })
    }

    let query = qs.parse(this.props.location.search, {
      ignoreQueryPrefix: true,
    })

    this.props.history.replace({
      ...this.props.location,
      search: qs.stringify(
        {
          ...query,
          keyword: this.state.searchKeyword || undefined,
        },
        { addQueryPrefix: true }
      ),
    })
  }

  loadMoreProducts = () => {
    this.props.loadMoreProducts().then(({ data, loading, error }) => {
      if (!error && !loading && data.products.length === 0) {
        this.setState({ hasMoreProducts: false })
      }
    })
  }

  helpAction = () => {
    this.props.history.push("/help")
  }

  onFilterChange = (key) => (e) => {
    if (e === null) e = { value: "", label: "All" }
    let query = qs.parse(this.props.location.search, {
      ignoreQueryPrefix: true,
    })
    if (e.value !== "") {
      this.props.trackEvent("Selected Marketplace filter", {
        filter: key,
        value: e.value,
      })
    }

    this.props.history.replace({
      ...this.props.location,
      search: qs.stringify(
        {
          ...query,
          [key]: e.value || undefined,
        },
        { addQueryPrefix: true }
      ),
    })

    this.setState({ hasMoreProducts: true })
  }

  renderSEO = () => {
    let url = "https://app.etr.ai/marketplace",
      title = `Reports | ETR`,
      seo_image = defaultLogo, //RT: TODO: GET IMAGES FOR ALL PAGES
      description =
        "Access 95+ reports, quarterly, analyzing 29 enterprise technology sectors & 540+ vendors. Track the mindshare of 4k+ Global 2000 tech leaders, and beyond."

    let SEO_Script = JSON.stringify({
      "@context": "http://schema.org",
      "@type": "WebSite",
      url: url,
      potentialAction: {
        "@type": "SearchAction",
        target: "https://app.etr.ai/marketplace?keyword={search_term_string}",
        "query-input": "required name=search_term_string",
      },
    })

    return (
      <Helmet>
        <title>{title}</title>
        <meta key="1" name="description" content={description} />
        <meta property="fb:pages" content="622251791269070" />
        <meta property="og:type" content="product.group" />
        <meta property="og:url" content={url} />
        <meta property="og:title" content={title} />
        <meta property="og:image" content={seo_image} />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:description" content={description} />
        <meta property="twitter:title" content={title} />
        <meta property="twitter:image" content={seo_image} />
        <script type="application/ld+json">{SEO_Script}</script>
      </Helmet>
    )
  }

  render = () => {
    let { location, data, loading, variables } = this.props
    let { isUserAuthenticated } = data.viewer
    let query = qs.parse(location.search, { ignoreQueryPrefix: true })
    let { keyword } = qs.parse(location.search, { ignoreQueryPrefix: true })
    let pathRegex = /^\/marketplace\/?$/i

    const {
      reportType: proprietaryDataset,
      filterPurchased,
      ...filters
    } = query
    const allowed = ["reportType", "vendor", "sector", "keyword"]
    const searchMetadata = filterObjectKeys(allowed, query)

    let noFilters =
      !proprietaryDataset &&
      !filters.vendor &&
      !filters.sector &&
      !filters.keyword

    return (
      <div className="marketplace-container">
        {this.renderSEO()}

        <div className="filter" data-cy="marketplace-filters">
          <div className="filter-container" data-cy="filters-selectors">
            <div className="filter-body">
              <SearchBar
                placeholder="Search Reports"
                onKeyUp={this.searchMarketPlace}
                defaultValue={
                  pathRegex.test(location.pathname)
                    ? keyword
                    : this.state.searchKeyword
                }
                onMagGlassClick={this.onMagGlassClick}
              />
              <div className="select-filter-group">
                <div className="select-filter">
                  <label>Vendor</label>
                  <Dropdown
                    selectAll={true}
                    defaultValue={filters.vendor}
                    onChange={this.onFilterChange("vendor")}
                    options={(data.vendors || []).map((v) => ({
                      name: v.name,
                      value: v.name,
                    }))}
                    onMenuScrollToBottom={this.props.loadMoreVendors}
                    onInputChange={this.props.searchVendors}
                    data-cy="vendor-filter-selector"
                  />
                </div>

                <div className="select-filter">
                  <label>Sector</label>
                  <Dropdown
                    selectAll={true}
                    defaultValue={filters.sector}
                    onChange={this.onFilterChange("sector")}
                    options={(data.sectors || []).map((s) => ({
                      name: s,
                      value: s,
                    }))}
                    data-cy="sector-filter-selector"
                  />
                </div>

                <div className="select-filter">
                  <label>Report Type</label>
                  <Dropdown
                    selectAll={true}
                    defaultValue={proprietaryDataset}
                    onChange={this.onFilterChange("reportType")}
                    options={(data.productTypes || []).map((productType) => {
                      let name = productType

                      if (productType === "DD") {
                        name = "Drill Down"
                      }

                      if (productType === "VENN") {
                        name = "Insights"
                      }

                      if (productType === "IndustryAnalysis") {
                        name = "Industry Analysis"
                      }

                      if (productType === "VendorFocus") {
                        name = "Vendor Focus"
                      }

                      if (productType === "DataReport") {
                        name = "Data Report"
                      }

                      if (productType === "ObservatoryReport") {
                        name = "Observatory Report"
                      }

                      return {
                        name: name,
                        value: productType,
                      }
                    })}
                    data-cy="report-filter-selector"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        {isUserAuthenticated && (
          <div className="options-container" data-disabled={noFilters}>
            <Favorite
              location={this.props.location}
              searchData={searchMetadata}
              favoriteType="SEARCH"
              variables={{
                searchKeyword: query.keyword,
                vendor: query.vendor,
                sector: query.sector,
                reportType: query.reportType,
              }}
              trackingLocation="Market Place Search"
            >
              <FavoriteButton
                refecth={this.props.refecth}
                messageIfFavorited="Search Saved"
                messageIfNotFavorited="Save Search"
              />
            </Favorite>
          </div>
        )}
        {!data.products || data.products.length === 0 ? (
          <div className="no-results">
            <div>
              <img
                src="https://s3.amazonaws.com/dev-etr/images/no_results_found.png"
                className="image"
              />
            </div>
            <div className="title">No results found</div>
            <div className="description">
              "We don't have a report matching these criteria!"
            </div>
            <div className="description">
              Let us know what you need and we will get in touch with you.
            </div>
            <div className="no-result-button">
              <Button
                style={{ paddingLeft: "80px", paddingRight: "80px" }}
                classes={["blue-btn", "rounded-square", "small-caps"]}
                action={this.helpAction}
              >
                HELP
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <div className="marketplace-products-list">
              {data.products &&
                data.products.map((product, i) => {
                  return (
                    <ProductCard
                      key={i}
                      loading={this.props.loading}
                      trackEvent={this.props.trackEvent}
                      {...product}
                      showFavoriteIcon={
                        data.viewer && data.viewer.isUserAuthenticated
                      }
                      productQuery={{ query: PRODUCTS, variables }}
                    />
                  )
                })}
            </div>
            {this.state.hasMoreProducts && !loading && (
              <div id="loading-more-products" data-cy="waypoint">
                <Waypoint onEnter={this.loadMoreProducts} />
                <Loading />
              </div>
            )}
          </div>
        )}
      </div>
    )
  }
}

class MarketplaceWithData extends Component {
  constructor(props) {
    super(props)

    this.state = {
      searchVendors: "",
      hasMoreVendors: true,
    }
  }

  searchVendors = (value) => {
    this.setState({
      searchVendors: value,
      hasMoreVendors: true,
    })
  }

  render() {
    let {
      reportType: proprietaryDataset,
      filterPurchased,
      ...query
    } = qs.parse(this.props.location.search, { ignoreQueryPrefix: true })
    const allowed = [
      "vendor",
      "sector",
      "keyword",
      "roles",
      "id",
      "upcomingEvents",
      "pastEvents",
      "eventDateRange",
      "transcript",
    ]

    const filters = filterObjectKeys(allowed, query)
    const variables = {
      filters: {
        ...filters,
        proprietaryDataset,
      },
      filterPurchased: filterPurchased === "true",
      search: this.state.searchVendors,
    }

    return (
      <Query query={PRODUCTS} variables={variables} fetchPolicy="network-only">
        {(queryProps) => {
          let { loading, error, data, fetchMore } = queryProps

          if (loading && (!data || !data.products)) {
            return <Loading />
          }

          if (!data.viewer || !data.viewer.isUserAuthenticated) {
            return (
              <Redirect
                to={{ pathname: "/login", state: { from: "/marketplace" } }}
              />
            )
          }

          const loadMoreVendors = () => {
            if (this.state.hasMoreVendors) {
              fetchMore({
                variables: {
                  starting_after_vendors:
                    data.vendors && data.vendors.length > 0
                      ? data.vendors[data.vendors.length - 1].id
                      : null,
                },
                updateQuery: (prev, { fetchMoreResult }) => {
                  if (!fetchMoreResult) return prev

                  return Object.assign({}, prev, {
                    vendors: [...prev.vendors, ...fetchMoreResult.vendors],
                  })
                },
              }).then(({ data, loading, error }) => {
                if (!error && !loading && data.vendors.length === 0) {
                  this.setState({ hasMoreVendors: false })
                }
              })
            }
          }

          const loadMoreProducts = () =>
            fetchMore({
              variables: {
                starting_after_products:
                  data.products && data.products.length > 0
                    ? data.products[data.products.length - 1].id
                    : null,
              },
              updateQuery: (prev, { fetchMoreResult }) => {
                if (!fetchMoreResult) return prev
                return Object.assign({}, prev, {
                  products: [...prev.products, ...fetchMoreResult.products],
                })
              },
            })

          return (
            <>
              {error && <p>Error while searching Products.</p>}
              <Marketplace
                searchVendors={this.searchVendors}
                hasMoreVendors={this.state.hasMoreVendors}
                {...this.props}
                {...queryProps}
                loadMoreProducts={loadMoreProducts}
                loadMoreVendors={loadMoreVendors}
              />
            </>
          )
        }}
      </Query>
    )
  }
}

export default MarketplaceWithData
